/**
 * BanReasonsManager.jsx — iter-269 (Feb 2026)
 * ============================================
 * Reusable CRUD UI for account ban reasons — one instance per platform:
 *
 *   • Merchant: reason_type="merchant_ban_reason"
 *   • CGC:      reason_type="cgc_ban_reason"
 *
 * Renders inside the platform's "القوائم المنسدلة" page.
 * Each row exposes: label_ar / label_en / active toggle / edit / delete.
 * The AI-translate button auto-fills `label_en` from `label_ar` using the
 * existing `/api/admin/legal-ai/translate` bot (Gemini). Locked entries
 * (default "Other/أخرى") cannot be renamed or deleted — only toggled.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Edit3, Trash2, Loader2, Save, X, Globe,
  Ban, Lock, ToggleRight, ToggleLeft, RefreshCw,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BanReasonsManager({
  reasonType,          // "merchant_ban_reason" | "cgc_ban_reason"
  language = 'ar',
  title,               // custom heading (optional)
  accentColor = 'red', // "red" | "amber"
}) {
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');

  // Editor modal state
  const [openEditor, setOpenEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [labelAr, setLabelAr] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [translating, setTranslating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/dropdown-reasons/${reasonType}`,
        { headers: getAuthHeaders() });
      setReasons(r.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [reasonType]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setLabelAr('');
    setLabelEn('');
    setIsActive(true);
    setOpenEditor(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setLabelAr(r.label_ar || '');
    setLabelEn(r.label_en || '');
    setIsActive(r.is_active !== false);
    setOpenEditor(true);
  };

  const doTranslate = async () => {
    const src = labelAr.trim();
    if (!src) {
      toast.error(t('اكتب النص العربي أولاً', 'Enter the Arabic text first'));
      return;
    }
    setTranslating(true);
    try {
      const r = await axios.post(`${API}/translate`,
        { text: src, direction: 'ar_to_en' },
        { headers: getAuthHeaders() });
      if (r.data?.success && r.data?.translated) {
        setLabelEn(r.data.translated);
        toast.success(t('تمت الترجمة بواسطة Gemini', 'Translated by Gemini'));
      } else {
        toast.error(t('فشلت الترجمة', 'Translation failed'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setTranslating(false);
    }
  };

  const save = async () => {
    const ar = labelAr.trim();
    if (!ar) {
      toast.error(t('اكتب السبب بالعربي', 'Enter the Arabic label'));
      return;
    }
    setBusy(editingId || 'new');
    try {
      const payload = { label_ar: ar, label_en: labelEn.trim(), is_active: isActive };
      if (editingId) {
        await axios.put(`${API}/admin/dropdown-reasons/${editingId}`, payload,
          { headers: getAuthHeaders() });
        toast.success(t('تم التحديث', 'Updated'));
      } else {
        await axios.post(`${API}/admin/dropdown-reasons/${reasonType}`, payload,
          { headers: getAuthHeaders() });
        toast.success(t('تمت الإضافة', 'Created'));
      }
      setOpenEditor(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy('');
    }
  };

  const toggleActive = async (r) => {
    setBusy(r.id);
    try {
      await axios.put(`${API}/admin/dropdown-reasons/${r.id}`,
        { label_ar: r.label_ar, label_en: r.label_en, is_active: !r.is_active },
        { headers: getAuthHeaders() });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy('');
    }
  };

  const remove = async (r) => {
    if (r.is_locked) {
      toast.error(t('لا يمكن حذف سبب مثبت', 'Cannot delete a locked reason'));
      return;
    }
    if (!window.confirm(t(`حذف "${r.label_ar}"؟`, `Delete "${r.label_ar}"?`))) return;
    setBusy(r.id);
    try {
      await axios.delete(`${API}/admin/dropdown-reasons/${r.id}`,
        { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy('');
    }
  };

  const accent = {
    red:   { border: 'border-red-500/30',   text: 'text-red-300',   bg: 'bg-red-500/10',   pill: 'bg-red-500/15 text-red-300' },
    amber: { border: 'border-amber-500/30', text: 'text-amber-300', bg: 'bg-amber-500/10', pill: 'bg-amber-500/15 text-amber-300' },
  }[accentColor] || {};

  return (
    <div className={`bg-white/[0.02] border ${accent.border} rounded-2xl p-5`} data-testid="ban-reasons-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className={`text-sm font-semibold flex items-center gap-2 ${accent.text}`}>
          <Ban className="h-4 w-4" />
          {title || t('أسباب حظر الحساب', 'Account Ban Reasons')}
          <span className="text-[10px] text-white/40">({reasons.length})</span>
        </h3>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load}
                  className="border-white/10 text-white/70 hover:bg-white/5"
                  data-testid="ban-reasons-refresh-btn">
            <RefreshCw className="h-3 w-3" />
          </Button>
          <Button size="sm" onClick={openCreate}
                  className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30"
                  data-testid="ban-reasons-add-btn">
            <Plus className="h-3 w-3 me-1" />
            {t('إضافة سبب', 'Add reason')}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-2">
          {[0, 1, 2].map((i) => (
            <div key={`ban-skel-${i}`} className="h-14 bg-white/[0.02] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : reasons.length === 0 ? (
        <p className="text-center py-8 text-[#71717A] text-[13px]">
          {t('لا توجد أسباب. أضف أول سبب.', 'No reasons yet. Add the first one.')}
        </p>
      ) : (
        <ul className="space-y-2">
          {reasons.map((r) => (
            <li key={r.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-colors
                  ${r.is_active ? 'border-white/10 bg-black/30' : 'border-white/5 bg-black/10 opacity-60'}`}
                data-testid={`ban-reason-row-${r.id}`}>
              {/* Locked badge */}
              {r.is_locked && (
                <span className="shrink-0 flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/50" title={t('مثبت', 'Locked')}>
                  <Lock className="h-2.5 w-2.5" />
                  {t('مثبت', 'Locked')}
                </span>
              )}
              {/* Labels */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-semibold truncate">{r.label_ar}</p>
                <p className="text-[11px] text-white/50 truncate" dir="ltr">{r.label_en}</p>
              </div>
              {/* Active toggle */}
              <button
                onClick={() => toggleActive(r)}
                disabled={busy === r.id}
                className={`h-7 w-7 rounded-lg flex items-center justify-center ${r.is_active ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-white/30 hover:bg-white/5'}`}
                title={r.is_active ? t('فعّال', 'Active') : t('معطّل', 'Disabled')}
                data-testid={`ban-reason-toggle-${r.id}`}
              >
                {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (r.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />)}
              </button>
              {/* Edit */}
              <button
                onClick={() => openEdit(r)}
                className="h-7 w-7 rounded-lg flex items-center justify-center text-cyan-300 hover:bg-cyan-500/10"
                title={t('تعديل', 'Edit')}
                data-testid={`ban-reason-edit-${r.id}`}
              >
                <Edit3 className="h-3.5 w-3.5" />
              </button>
              {/* Delete */}
              <button
                onClick={() => remove(r)}
                disabled={r.is_locked || busy === r.id}
                className="h-7 w-7 rounded-lg flex items-center justify-center text-red-300 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed"
                title={r.is_locked ? t('لا يُحذف', 'Locked') : t('حذف', 'Delete')}
                data-testid={`ban-reason-delete-${r.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Editor modal */}
      <Dialog open={openEditor} onOpenChange={(o) => { if (!o) setOpenEditor(false); }}>
        <DialogContent className="bg-[#0f0f1e] border-[#2a2a4e] text-white max-w-md"
                       dir={isAr ? 'rtl' : 'ltr'}
                       data-testid="ban-reason-editor-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ban className="h-4 w-4 text-red-400" />
              {editingId ? t('تعديل سبب', 'Edit reason') : t('إضافة سبب جديد', 'Add new reason')}
            </DialogTitle>
            <DialogDescription className="text-xs text-white/60">
              {t(
                'اكتب السبب بالعربية، ثم اضغط "ترجم" لملء الحقل الإنجليزي تلقائياً بواسطة Gemini.',
                'Enter the Arabic label, then click "Translate" to auto-fill the English one via Gemini.',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-[11px] text-white/60 mb-1 block">{t('السبب بالعربية', 'Arabic label')}</label>
              <Input
                value={labelAr}
                onChange={(e) => setLabelAr(e.target.value)}
                placeholder={t('مثال: مخالفة القوانين', 'e.g. Terms violation')}
                className="bg-[#050505] border-white/10 text-white"
                dir="rtl"
                data-testid="ban-reason-label-ar"
                autoFocus
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] text-white/60">{t('السبب بالإنجليزية', 'English label')}</label>
                <Button size="sm" variant="ghost" onClick={doTranslate} disabled={translating || !labelAr.trim()}
                        className="h-6 px-2 text-[10px] text-cyan-300 hover:bg-cyan-500/10"
                        data-testid="ban-reason-translate-btn">
                  {translating ? <Loader2 className="h-3 w-3 animate-spin me-1" /> : <Globe className="h-3 w-3 me-1" />}
                  {t('ترجم بالذكاء الاصطناعي', 'AI Translate')}
                </Button>
              </div>
              <Input
                value={labelEn}
                onChange={(e) => setLabelEn(e.target.value)}
                placeholder="e.g. Terms violation"
                className="bg-[#050505] border-white/10 text-white"
                dir="ltr"
                data-testid="ban-reason-label-en"
              />
            </div>
            <label className="flex items-center gap-2 text-[12px] text-white/70 select-none cursor-pointer">
              <input type="checkbox" checked={isActive}
                     onChange={(e) => setIsActive(e.target.checked)}
                     data-testid="ban-reason-active-checkbox"
                     className="accent-emerald-500" />
              {t('فعّال (يظهر في نافذة الحظر)', 'Active (shown in the block modal)')}
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenEditor(false)}
                    className="border-white/10 text-white/70"
                    data-testid="ban-reason-cancel-btn">
              <X className="h-3.5 w-3.5 me-1" />
              {t('إلغاء', 'Cancel')}
            </Button>
            <Button onClick={save} disabled={!labelAr.trim() || !!busy}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                    data-testid="ban-reason-save-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 me-1 animate-spin" /> : <Save className="h-3.5 w-3.5 me-1" />}
              {editingId ? t('حفظ', 'Save') : t('إضافة', 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
